// REST for the BLE lamp tree — the endpoints in the backend's api/new/bleNodes.js.
//
// Same axios instance and base URL as api.js. NOT local-first, on purpose: the
// radio identity and the lock state must come from the server every time, or
// a lock set by the owner five minutes ago would still be honoured on a phone
// that cached the old row.
//
// Every reply is { ok, ... }; a refusal is a non-2xx with { ok:false, message }.
// The helper unwraps that into a thrown Error carrying the server's own words.

import { post, getDeviceDetail } from './api';
import { fullSync } from '../db/syncService';
import { dbSupported } from '../db/schema';
import { markUser, markUsersOfBleNode, markUsersOfDevice } from '../db/markSinkData';

async function call(endpoint, body) {
  try {
    const res = await post(endpoint, body);
    if (res?.data?.ok === false) {
      const e = new Error(res.data.message || `${endpoint} failed`);
      e.status = res.status;
      throw e;
    }
    return res.data;
  } catch (err) {
    if (err?.response?.data?.message) {
      const e = new Error(err.response.data.message);
      e.status = err.response.status;
      throw e;
    }
    throw err;
  }
}

/** -> { node, ancestors, children, lamps } — lamps carry identity unless locked. */
export const getBleNode = (userId, uniqueNumber) =>
  call('GetBleNodeByUniqueNumber', { UserId: userId, UniqueNumber: uniqueNumber });

export const getBleNodeById = (userId, nodeId) =>
  call('GetBleNodeById', { UserId: userId, NodeId: nodeId });

/** Parents and groups visible to this user at a location. -> { nodes } */
export const getBleNodesAtLocation = (userId, locationId, nodeType) =>
  call('GetBleNodesAtLocation', { UserId: userId, LocationID: locationId, NodeType: nodeType });

/**
 * Creates a parent or group (DeviceID 100 row + mapping + node), then resyncs
 * the offline cache.
 *
 * ⚠️ THE RESYNC IS LOAD-BEARING. Room lists on a phone are served local-first
 * from SQLite, and the cache is rebuilt only by fullSync — at launch when the
 * server flags SinkData, or by hand from the Me tab. Without it the new device
 * existed on the server and in no room on the phone that created it
 * (Factory > Office, 2026-09-15). Fix it here, never in a screen.
 */
export const addBleNode = async (userId, { nodeType, name, locationId, roomId, parentNodeId, codec }) => {
  const res = await call('AddBleNode', {
    UserId: userId,
    NodeType: nodeType,
    Name: name,
    LocationID: locationId,
    RoomID: roomId || 0,
    ParentNodeId: parentNodeId ?? null,
    Codec: codec,
  });
  // This phone rebuilds now; anywhere else (the desktop) the creator is flagged
  // so their phone pulls the new device at its next launch.
  if (dbSupported()) await fullSync(userId).catch(() => {});
  else await markUser(userId);
  return res;
};

/**
 * Reserves an identity for a new lamp under a group. -> { lamp }
 * A Bluetooth Mesh group passes `mesh` = { MeshAddress, MeshElements,
 * MeshDevKey, MeshUuid?, MeshMac? } — the lamp is already paired by then.
 */
export const addBleLamp = (userId, groupNodeId, name, roomId, mesh = null) =>
  call('AddBleLamp', {
    UserId: userId, ParentNodeId: groupNodeId, Name: name, RoomID: roomId, ...(mesh || {}),
  });

/**
 * Bluetooth Mesh: the location's network keys + THIS phone's own source
 * address (pass the one it already holds, if any). -> { network }
 */
export const getMeshNetwork = (userId, nodeId, clientAddress) =>
  call('GetMeshNetwork', { UserId: userId, NodeId: nodeId, ClientAddress: clientAddress || null });

/** Bluetooth Mesh: reserve `count` unicast addresses for a lamp being paired. -> { address } */
export const allocateMeshAddress = (userId, groupNodeId, count) =>
  call('AllocateMeshAddress', { UserId: userId, ParentNodeId: groupNodeId, Count: count });

/**
 * { Name?, RoomID?, SortOrder?, ParentNodeId?, Codec?, IncludeFlags?, HoldMs? }
 * Mesh lamps also: { MeshComposition?, MeshTempMin?, MeshTempMax?, MeshConfigured?, MeshMac? }
 */
export const updateBleNode = async (userId, nodeId, data) => {
  const res = await call('UpdateBleNode', { UserId: userId, NodeId: nodeId, ...data });
  // A rename or a room move changes what the room screen shows — here, and on
  // every other phone with access to this node.
  if (data.Name != null || data.RoomID != null || data.ParentNodeId !== undefined) {
    await fullSync(userId).catch(() => {});
    await markUsersOfBleNode(nodeId);
  }
  return res;
};

export const deleteBleNode = async (userId, nodeId, { isDevice = false } = {}) => {
  const res = await call('DeleteBleNode', { UserId: userId, NodeId: nodeId });
  if (isDevice) {
    await fullSync(userId).catch(() => {});
    // The mappings are deactivated, not deleted, so the audience still resolves.
    await markUsersOfBleNode(nodeId);
  }
  return res;
};

/** Owner only; a lock covers the subtree. */
export const setBleNodeLock = (userId, nodeId, locked) =>
  call('SetBleNodeLock', { UserId: userId, NodeId: nodeId, Locked: locked ? 1 : 0 });

/** After a loop: `lamps` = [{ NodeId, TxCount, LastValues? }]. */
export const recordBleNodeCommand = (userId, nodeId, command, payload, result, lamps) =>
  call('RecordBleNodeCommand', {
    UserId: userId, NodeId: nodeId, Command: command, Payload: payload ?? null, Result: result, Lamps: lamps || [],
  });

export const getBleNodeCommandLog = (userId, nodeId, limit = 100) =>
  call('GetBleNodeCommandLog', { UserId: userId, NodeId: nodeId, Limit: limit });

/**
 * Switch off an ORPHAN DeviceID 100 record — a device row with no BleNode
 * behind it (BL85149340 lost its group in the tree migration), so the group
 * screens, and DeleteBleNode, cannot reach it. The same soft delete a group
 * delete does: DevicesInstalled.Status = 0 and every mapping IsActive = 0.
 * Nothing is erased, so it can be undone in the database.
 *
 * ⚠️ ONLY for DeviceID 100 rows the server has just said have no node; the
 * caller checks that, and the UI offers it only to a Super Admin or the owner.
 */
export const removeOrphanBleRecord = async (device, actorUserId) => {
  const uniqueNumber = String(device?.UniqueNumber || '');
  if (!uniqueNumber) throw new Error('No unique number');
  let id = device.ID;
  if (!id) {
    const res = await getDeviceDetail(uniqueNumber);
    const row = Array.isArray(res) ? res[0] : (Array.isArray(res?.data) ? res.data[0] : res?.data); // eslint-disable-line no-nested-ternary
    id = row?.ID;
  }
  if (!id) throw new Error('Could not find this record');
  const off = await post('UpdateDeviceInstalled', {
    data: { Status: 0 },
    where: `ID = ${Number(id)} AND DeviceID = 100`,
  });
  if (off?.data?.status && String(off.data.status) !== '200') throw new Error(off.data.message || 'Could not remove it');
  // Before the mappings go: the audience is resolved through them.
  await markUsersOfDevice(uniqueNumber);
  await post('UpdateUserDeviceMapping', {
    data: { IsActive: 0 },
    where: `DeviceInstalledID = ${Number(id)}`,
  });
  if (dbSupported()) await fullSync(actorUserId).catch(() => {});
};
